import { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../../components/CustomSelect.jsx";
import {
    fetchManagerSeasonDriverOptions,
    fetchManagerSeriesSeasons,
} from "../../../lib/managerApi.js";
import {
    getJob,
    runDriverClassChangeDb,
    streamJobLogs,
} from "../../../lib/ligamanagerApi.js";

const CLASS_OPTIONS = [
    { value: "PRO", label: "PRO" },
    { value: "INT", label: "INT" },
    { value: "AM", label: "AM" },
];

function prettyErr(error) {
    if (!error) return "Unbekannter Fehler";
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message || "Fehler";
    if (typeof error === "object") {
        if (error.detail) return typeof error.detail === "string" ? error.detail : JSON.stringify(error.detail);
        if (error.message) return error.message;
    }
    return String(error);
}

function carNumSort(a, b) {
    const na = Number.parseInt(String(a?.car_number || "").replace("#", ""), 10);
    const nb = Number.parseInt(String(b?.car_number || "").replace("#", ""), 10);

    const aa = Number.isFinite(na) ? na : 999999;
    const bb = Number.isFinite(nb) ? nb : 999999;

    if (aa !== bb) return aa - bb;

    return String(a?.driver_name || a?.label || "").localeCompare(
        String(b?.driver_name || b?.label || ""),
        "de",
        { numeric: true, sensitivity: "base" },
    );
}

function classPillClass(classKey) {
    const key = String(classKey || "").toUpperCase();
    if (key === "PRO") return "pill--pro";
    if (key === "INT") return "pill--int";
    if (key === "AM") return "pill--am";
    return "pill--gesamt";
}

function driverName(driver) {
    return (
        driver?.driver_name ||
        driver?.display_name ||
        driver?.iracing_name ||
        driver?.name ||
        driver?.label ||
        driver?.driver?.display_name ||
        driver?.driver?.iracing_name ||
        "Unbekannt"
    );
}

export default function DriverClass() {
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [season, setSeason] = useState(null);
    const [drivers, setDrivers] = useState([]);

    const [seasonDriverId, setSeasonDriverId] = useState("");
    const [newClass, setNewClass] = useState("");
    const [newCarNumber, setNewCarNumber] = useState("");

    const [jobId, setJobId] = useState(null);
    const [job, setJob] = useState(null);
    const [logs, setLogs] = useState([]);

    const selectedDriver = useMemo(() => {
        return drivers.find((d) => {
            const id = d.season_driver_id || d.id;
            return String(id) === String(seasonDriverId);
        }) || null;
    }, [drivers, seasonDriverId]);

    const driverOptions = useMemo(() => {
        return [...drivers].sort(carNumSort).map((d) => {
            const id = d.season_driver_id || d.id;
            const carNumber = String(d.car_number || "").replace("#", "").trim() || "-";
            const cls = String(d.class_name || d.class_key || "-").toUpperCase();
            const name = driverName(d);

            return {
                value: id,
                label: `#${carNumber} · ${name} · ${cls}`,
            };
        });
    }, [drivers]);

    const jobStatus = String(job?.status || job?.state || "").trim();

    const sectionStyle = {
        border: "1px solid var(--border-soft)",
        borderRadius: 18,
        padding: 16,
        background: "rgba(232,253,83,.045)",
    };

    const labelStyle = {
        display: "grid",
        gap: 7,
        minWidth: 0,
    };

    const labelTextStyle = {
        fontWeight: 800,
        fontSize: 13,
        letterSpacing: ".03em",
        textTransform: "uppercase",
        color: "var(--text)",
    };

    useEffect(() => {
        let alive = true;

        async function load() {
            setLoading(true);
            setError("");

            try {
                const data = await fetchManagerSeriesSeasons();
                const seriesRows = Array.isArray(data?.series) ? data.series : [];

                const gt3xSeries =
                    seriesRows.find((row) => String(row.series_key || "").toLowerCase() === "gt3x") ||
                    seriesRows[0];

                if (!gt3xSeries) {
                    throw new Error("Keine Serie gefunden.");
                }

                const seasons = Array.isArray(gt3xSeries.seasons) ? gt3xSeries.seasons : [];

                const activeSeason =
                    seasons.find((s) => String(s.status || "").toLowerCase() === "active") ||
                    seasons[0];

                if (!activeSeason) {
                    throw new Error("Keine aktive Saison gefunden.");
                }

                const nextSeasonId = activeSeason.id || activeSeason.season_id;

                const driversData = await fetchManagerSeasonDriverOptions({
                    season_id: Number(nextSeasonId),
                });

                const rows = Array.isArray(driversData?.season_drivers)
                    ? driversData.season_drivers
                    : [];

                const sortedRows = [...rows].sort(carNumSort);

                if (!alive) return;

                setSeason(activeSeason);
                setDrivers(sortedRows);
                setSeasonDriverId(
                    sortedRows[0]
                        ? String(sortedRows[0].season_driver_id || sortedRows[0].id)
                        : "",
                );
            } catch (err) {
                if (alive) setError(prettyErr(err));
            } finally {
                if (alive) setLoading(false);
            }
        }

        load();

        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        if (!selectedDriver) return;

        setNewClass(String(selectedDriver.class_name || "").toUpperCase());
        setNewCarNumber(String(selectedDriver.car_number || "").replace("#", ""));
    }, [selectedDriver]);

    async function refreshJob(id) {
        if (!id) return;

        try {
            const data = await getJob(id);
            setJob(data?.job || data);
        } catch {
            // nicht blockieren
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();

        setError("");
        setSuccess("");
        setLogs([]);
        setJob(null);
        setJobId(null);

        if (!seasonDriverId) {
            setError("Bitte Fahrer auswählen.");
            return;
        }

        if (!newClass) {
            setError("Bitte neue Klasse auswählen.");
            return;
        }

        if (!String(newCarNumber || "").trim()) {
            setError("Bitte neue Startnummer eingeben.");
            return;
        }

        const ok = window.confirm(
            "Klassenwechsel wirklich für die komplette Saison rückwirkend ausführen?\n\n" +
            "Das ändert Ergebnis-Snapshots, berechnet Race Points und Standings neu.",
        );

        if (!ok) return;

        setBusy(true);

        try {
            const data = await runDriverClassChangeDb({
                series_key: "gt3x",
                season_id: season?.id || season?.season_id,
                season_key: season?.season_key,
                season_driver_id: Number(seasonDriverId),
                new_class: newClass,
                new_car_number: String(newCarNumber).replace("#", "").trim(),
            });

            const id = data?.job_id;
            setJobId(id);
            setSuccess(`Job gestartet: #${id}`);

            if (id) {
                refreshJob(id);

                streamJobLogs({
                    job_id: id,
                    onLog: (log) => {
                        setLogs((prev) => [...prev, log]);
                    },
                    onDone: () => {
                        refreshJob(id);
                        setBusy(false);
                    },
                    onError: (err) => {
                        setError(prettyErr(err));
                        setBusy(false);
                    },
                });
            } else {
                setBusy(false);
            }
        } catch (err) {
            setError(prettyErr(err));
            setBusy(false);
        }
    }

    return (
        <div className="rv-page manager-modern-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Liga Manager</div>
                        <h1 className="rv-page-title">Driver Class</h1>
                        <p className="rv-page-subtitle">
                            Fahrer rückwirkend für die komplette Saison in eine andere Klasse setzen.
                            DB-only Workflow ohne Sheets und ohne Neuimport.
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <span className="pill pill--gesamt">GT3X</span>
                        {season && (
                            <span className="pill pill--int">
                                {season.display_name || season.season_key || `Saison ${season.id}`}
                            </span>
                        )}
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}
            {success && <div className="rv-message rv-message--ok">{success}</div>}

            <section className="card rv-filter-card" style={{ display: "grid", gap: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                        <h2 style={{ margin: 0 }}>Klassenwechsel</h2>
                        <p className="muted" style={{ margin: 0 }}>
                            Wähle den Fahrer, die neue Klasse und die neue Startnummer. Danach startet ein Worker-Job.
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <span className="pill pill--gesamt">
                            Fahrer: {drivers.length}
                        </span>
                        {loading && <span className="pill pill--int">Lädt...</span>}
                        {busy && <span className="pill pill--pro">Job läuft</span>}
                    </div>
                </div>

                <div style={sectionStyle}>
                    {season && (
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                            <span className="muted">Aktive Saison</span>
                            <strong>{season.display_name || season.season_key || season.id}</strong>
                        </div>
                    )}

                    {loading && (
                        <p className="muted" style={{ margin: 0 }}>
                            Lade aktive Saison und Fahrer...
                        </p>
                    )}

                    <form
                        onSubmit={handleSubmit}
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                            gap: 14,
                            alignItems: "end",
                        }}
                    >
                        <label style={labelStyle}>
                            <span style={labelTextStyle}>Fahrer</span>
                            <CustomSelect
                                value={seasonDriverId}
                                onChange={setSeasonDriverId}
                                options={driverOptions}
                                placeholder="Fahrer auswählen"
                                disabled={busy || loading}
                            />
                        </label>

                        <label style={labelStyle}>
                            <span style={labelTextStyle}>Neue Klasse</span>
                            <CustomSelect
                                value={newClass}
                                onChange={setNewClass}
                                options={CLASS_OPTIONS}
                                placeholder="Klasse auswählen"
                                allowEmpty={false}
                                disabled={busy || loading}
                            />
                        </label>

                        <label style={labelStyle}>
                            <span style={labelTextStyle}>Neue Startnummer</span>
                            <input
                                className="input"
                                value={newCarNumber}
                                onChange={(e) => setNewCarNumber(e.target.value)}
                                placeholder="z.B. 51"
                                disabled={busy || loading}
                            />
                        </label>

                        <button className="btn btn--accent" type="submit" disabled={busy || loading}>
                            {busy ? "Job läuft..." : "Klassenwechsel starten"}
                        </button>
                    </form>
                </div>
            </section>

            {selectedDriver && (
                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>Vorschau</h2>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                            gap: 12,
                        }}
                    >
                        <div style={sectionStyle}>
                            <div className="muted" style={{ marginBottom: 8 }}>Aktuell</div>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <span className={`pill ${classPillClass(selectedDriver.class_name)}`}>
                                    {selectedDriver.class_name || "-"}
                                </span>
                                <strong>
                                    #{selectedDriver.car_number || "-"} · {driverName(selectedDriver)}
                                </strong>
                            </div>
                        </div>

                        <div style={sectionStyle}>
                            <div className="muted" style={{ marginBottom: 8 }}>Neu</div>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <span className={`pill ${classPillClass(newClass)}`}>
                                    {newClass || "-"}
                                </span>
                                <strong>
                                    #{newCarNumber || "-"} · {driverName(selectedDriver)}
                                </strong>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {jobId && (
                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                        <div>
                            <h2 style={{ margin: 0 }}>Job #{jobId}</h2>
                            <p className="muted" style={{ margin: 0 }}>
                                Worker-Logs für den rückwirkenden Klassenwechsel.
                            </p>
                        </div>

                        <span className={`pill ${jobStatus === "done" ? "pill--am" : jobStatus === "error" ? "pill--pro" : "pill--int"}`}>
                            {jobStatus || "running"}
                        </span>
                    </div>

                    <div className="logbox" style={{ minHeight: 120, maxHeight: 360, overflow: "auto" }}>
                        {!logs.length && (
                            <div className="muted">Noch keine Logs vorhanden.</div>
                        )}

                        {logs.map((line, idx) => (
                            <div key={idx}>
                                {typeof line === "string" ? line : JSON.stringify(line)}
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}